import { deepClonePoints } from "../../presentation/services/CloningService/point";
import { getDate } from "../../utils/date/date";

export class PointMap {
    static generateMap(points, map = new Map(), options = {}) {
        // Fonte sem serie, falha do calculatrix, resposta vazia: em todos esses casos o mapa
        // certo e o vazio. Iterar `undefined` aqui derrubava a tela inteira.
        for (const point of Array.isArray(points) ? points : []) {
            //const { s: serieId, sc: scenarioId, period: period } = point;
            const serieId = point.s;
            let scenarioId = point.sc;
            const period = point.period;

            const time = getDate(point.t)
                ? getDate(point.t).getTime().toString()
                : "";

            const timeMap = PointMap.getOrCreateTimeMap(map, serieId);
            if (options.projection) {
                point.sc = options.scenarioIdBase;
                scenarioId = options.scenarioIdBase;
            }
            PointMap.addPointToMap(timeMap, time, scenarioId, period, point);
        }

        return map;
    }

    static getOrCreateTimeMap(map, serieId) {
        const existingMap = map.has(serieId) ? map.get(serieId) : new Map();
        map.set(serieId, existingMap);
        return existingMap;
    }

    static getOrCreateScenarioPeriodMap(map, time) {
        const existingMap = map.has(time) ? map.get(time) : new Map();
        map.set(time, existingMap);
        return existingMap;
    }

    static addPointToMap(map, time, scenarioId, period, point) {
        const scenarioPeriodKey = this.getScenarioPeriodKey(scenarioId, period);
        const scenarioPeriodMap = PointMap.getOrCreateScenarioPeriodMap(
            map,
            time
        );

        scenarioPeriodMap.set(scenarioPeriodKey, [
            {
                ...point,
                t: getDate(point.t)
            }
        ]);
    }

    static findPoint(map, serie, scenarioId, period, date) {
        const points = this.findPoints(map, [serie], scenarioId, period, date);
        return points[0];
    }

    static findPoints(
        map,
        series,
        scenarioId,
        period,
        date,
        visualizationAccumulated = undefined // keep undefined on default; if false will calculate
    ) {
        if (!map) return [];
        const seriesIds = series.map((s) => s._id).filter((s) => s);

        const scenarioPeriodKey = this.getScenarioPeriodKey(scenarioId, period);
        const points = [];
        if (period !== "monthly") visualizationAccumulated = true;

        for (const serieId of seriesIds) {
            const timeMap = map.get(serieId);
            if (!timeMap) continue;

            const currentSerie = series.find((s) => s._id === serieId);
            const { metadata } = currentSerie;
            const currentAccumulated = metadata ? metadata.accumulated : false;

            if (date) {
                const time = date.getTime().toString();
                const scenarioPeriodMap = timeMap.get(time);
                const seriePoints = scenarioPeriodMap
                    ? PointMap.getClonedPointsFromMapByKey(
                          scenarioPeriodMap,
                          scenarioPeriodKey,
                          visualizationAccumulated,
                          currentAccumulated
                      )
                    : [];
                points.push(...seriePoints);
            } else {
                for (let [_time, scenarioPeriodMap] of timeMap) {
                    const seriePoints = PointMap.getClonedPointsFromMapByKey(
                        scenarioPeriodMap,
                        scenarioPeriodKey,
                        visualizationAccumulated,
                        currentAccumulated
                    );
                    points.push(...seriePoints);
                }
            }
        }
        return points;
    }

    static findPointsInTimeRange(
        map,
        series,
        scenarioId,
        period,
        timesBetween,
        visualizationAccumulated = undefined // keep undefined on default; if false will calculate
    ) {
        if (!map) return [];
        const seriesIds = series.map((s) => s._id).filter((s) => s);
        const scenarioPeriodKey = this.getScenarioPeriodKey(scenarioId, period);
        if (period !== "monthly") visualizationAccumulated = true;
        const points = [];

        for (const serieId of seriesIds) {
            const timeMap = map.get(serieId);
            if (!timeMap) continue;

            const currentSerie = series.find((s) => s._id === serieId);
            const { metadata } = currentSerie;
            let currentAccumulated = metadata ? metadata.accumulated : false;
            if (currentSerie?.metadata?.origin === "goals")
                currentAccumulated = true;

            for (let [time, scenarioPeriodMap] of timeMap) {
                const seriePoints = PointMap.getClonedPointsFromMapByKey(
                    scenarioPeriodMap,
                    scenarioPeriodKey,
                    visualizationAccumulated,
                    currentAccumulated
                );

                if (timesBetween) {
                    const { startDate, endDate } = timesBetween;
                    if (
                        time >= startDate.getTime().toString() &&
                        time <= endDate.getTime().toString()
                    ) {
                        points.push(...seriePoints);
                    }
                } else {
                    points.push(...seriePoints);
                }
            }
        }
        return points;
    }

    static getScenarioPeriodKey(scenarioId, period) {
        return `${scenarioId}_${period}`;
    }

    static getClonedPointsFromMapByKey(
        map,
        scenarioPeriodKey,
        visualizationAccumulated,
        currentAccumulated
    ) {
        const seriePoints = map.get(scenarioPeriodKey) || [];
        const clonedPoints = deepClonePoints(seriePoints);

        let metric = "m";
        if (
            typeof visualizationAccumulated === "boolean" &&
            visualizationAccumulated === true &&
            !currentAccumulated
        )
            metric = "a";

        PointMap.filterPointsByMetric(clonedPoints, metric);
        PointMap.switchPointsMetric(clonedPoints, metric);

        return clonedPoints;
    }

    static switchPointsMetric(points, metric) {
        for (const point of points) {
            if (metric === "a" && point.a) point.m = point.a;
            point.a = undefined;
        }
    }

    static filterPointsByMetric(points, metric) {
        return points.filter((p) => {
            if (metric === "a") return p.a;
            return !p.a;
        });
    }
}
